import { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

const DEFAULT_FORM = {
  federal_tax_reserve_rate_pct: 0,
  state_local_tax_reserve_rate_pct: 0,
  operations_payout_destination: '',
  payroll_payout_destination: '',
  taxes_payout_destination: '',
  auto_split_payouts_enabled: false,
};

function normalizeRate(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.min(Math.max(numeric, 0), 100);
}

function PayoutRoutingSettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess('');

    const { data, error: readError } = await supabaseClient
      .from('org_settings')
      .select('org_id, federal_tax_reserve_rate_pct, state_local_tax_reserve_rate_pct, operations_payout_destination, payroll_payout_destination, taxes_payout_destination, auto_split_payouts_enabled')
      .limit(1)
      .maybeSingle();

    if (readError) {
      setError(readError.message);
      setLoading(false);
      return;
    }

    if (!data) {
      setError('No org_settings row found. Initialize org_settings for your organization before editing payout routing settings.');
      setLoading(false);
      return;
    }

    setOrgId(data.org_id);
    setForm({
      federal_tax_reserve_rate_pct: normalizeRate(data.federal_tax_reserve_rate_pct),
      state_local_tax_reserve_rate_pct: normalizeRate(data.state_local_tax_reserve_rate_pct),
      operations_payout_destination: data.operations_payout_destination || '',
      payroll_payout_destination: data.payroll_payout_destination || '',
      taxes_payout_destination: data.taxes_payout_destination || '',
      auto_split_payouts_enabled: Boolean(data.auto_split_payouts_enabled),
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));

  const save = async () => {
    if (!orgId) return;

    setSaving(true);
    setError(null);
    setSuccess('');

    const payload = {
      org_id: orgId,
      federal_tax_reserve_rate_pct: normalizeRate(form.federal_tax_reserve_rate_pct),
      state_local_tax_reserve_rate_pct: normalizeRate(form.state_local_tax_reserve_rate_pct),
      operations_payout_destination: form.operations_payout_destination.trim() || null,
      payroll_payout_destination: form.payroll_payout_destination.trim() || null,
      taxes_payout_destination: form.taxes_payout_destination.trim() || null,
      auto_split_payouts_enabled: Boolean(form.auto_split_payouts_enabled),
    };

    const { error: upsertError } = await supabaseClient
      .from('org_settings')
      .upsert(payload, { onConflict: 'org_id' });

    if (upsertError) {
      setError(upsertError.message);
    } else {
      setSuccess('Payout split settings saved.');
    }

    setSaving(false);
  };

  if (loading) {
    return <p className="text-sm text-gray-600">Loading payout split settings…</p>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-5">
      <div>
        <h3 className="text-xl font-bold text-gray-900">Tax Reserve & Deposit Routing</h3>
        <p className="text-sm text-gray-600 mt-1">
          Configure federal/state reserve percentages and destination bank-account IDs used by Stripe manual/API payout routing.
        </p>
      </div>

      {error && <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {success && <div className="rounded border border-green-300 bg-green-50 p-3 text-sm text-green-700">{success}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="text-sm">
          <span className="block font-medium text-gray-900 mb-1">Federal reserve %</span>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.federal_tax_reserve_rate_pct}
            onChange={(event) => set('federal_tax_reserve_rate_pct', event.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2"
          />
        </label>

        <label className="text-sm">
          <span className="block font-medium text-gray-900 mb-1">State/Local reserve %</span>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.state_local_tax_reserve_rate_pct}
            onChange={(event) => set('state_local_tax_reserve_rate_pct', event.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2"
          />
        </label>

        <label className="text-sm md:col-span-2">
          <span className="block font-medium text-gray-900 mb-1">Operations destination (Stripe external account ID)</span>
          <input
            type="text"
            value={form.operations_payout_destination}
            onChange={(event) => set('operations_payout_destination', event.target.value)}
            placeholder="ba_xxx"
            className="w-full rounded border border-gray-300 px-3 py-2"
          />
        </label>

        <label className="text-sm md:col-span-2">
          <span className="block font-medium text-gray-900 mb-1">Payroll destination (Stripe external account ID)</span>
          <input
            type="text"
            value={form.payroll_payout_destination}
            onChange={(event) => set('payroll_payout_destination', event.target.value)}
            placeholder="ba_xxx"
            className="w-full rounded border border-gray-300 px-3 py-2"
          />
        </label>

        <label className="text-sm md:col-span-2">
          <span className="block font-medium text-gray-900 mb-1">Taxes destination (Stripe external account ID)</span>
          <input
            type="text"
            value={form.taxes_payout_destination}
            onChange={(event) => set('taxes_payout_destination', event.target.value)}
            placeholder="ba_xxx"
            className="w-full rounded border border-gray-300 px-3 py-2"
          />
        </label>
      </div>

      <label className="inline-flex items-center gap-3 text-sm text-gray-900">
        <input
          type="checkbox"
          checked={form.auto_split_payouts_enabled}
          onChange={(event) => set('auto_split_payouts_enabled', event.target.checked)}
          className="h-4 w-4"
        />
        Enable automatic Stripe destination payout routing when split calculation runs
      </label>

      <div className="pt-2">
        <button
          onClick={save}
          disabled={saving || !orgId}
          className="px-5 py-2 rounded bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Split Settings'}
        </button>
      </div>
    </div>
  );
}

export default PayoutRoutingSettingsPanel;
